import { useState } from "react";
import type Application from "../../types/application";
import CompanyLogo from "@components/company_logo";
import { Body, SectionHeader, SectionTitle } from "@sprout/typography";
import CheckMarkCircleIcon from "@sprout/icons/check_mark_circle";
import { Row, Stack } from "@sprout/layout";
import useTranslation from "@hooks/use_translation";
import { Tag } from "@sprout/tag.tsx";
import theme from "@/stylesheets/theme.module.scss";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@components/ui/hover_card";
import HelpPopover from "@sprout/help_popover.tsx";
import ApplicationEllipsis from "@components/applications/application_ellipsis.tsx";
import { IconButton } from "@sprout/icon_button.tsx";
import DreamJobIcon from "@/sprout/icons/dream_job";
import Tooltip from "@/sprout/tooltip";
import DreamJobModal from "./dream_job_modal";
import { daysUntilNextMonth } from "@/utils/date_utils";

type Props = {
  application: Application;
  hasTokens: boolean;
  onAddDreamJob: () => void;
};

const DreamJobButton = ({ application, hasTokens, onAddDreamJob }: Props) => {
  const { t } = useTranslation("applications");
  const [isDreamJobModalOpen, setIsDreamJobModalOpen] = useState(false);

  if (application.dream_job || application.inactive || application.duplicate) {
    return null;
  }

  const tooltipText = hasTokens
    ? t("dream_job.mark_tooltip")
    : t("dream_job.no_tokens_tooltip", { count: daysUntilNextMonth() });

  return (
    <>
      <Tooltip text={tooltipText}>
        <IconButton
          icon={DreamJobIcon}
          size="md"
          label={tooltipText}
          disabled={!hasTokens}
          onClick={() => setIsDreamJobModalOpen(true)}
        />
      </Tooltip>
      <DreamJobModal
        application={application}
        afterSuccess={onAddDreamJob}
        open={isDreamJobModalOpen}
        onOpenChange={setIsDreamJobModalOpen}
      />
    </>
  );
};

const ApplicationHeader = (props: Props) => {
  const { application } = props;
  const { t } = useTranslation("applications");

  return (
    <Row gap="24px" align="flex-start">
      <div className="flex flex-col md:flex-row w-full gap-2 md:gap-6 content-center">
        <div className="hidden md:block">
          <CompanyLogo
            logoUrl={application.company_logo_url}
            companyName={application.company_name}
            size="large"
          />
        </div>
        <div className="flex flex-row items-center md:hidden">
          <div className="grow">
            <CompanyLogo
              logoUrl={application.company_logo_url}
              companyName={application.company_name}
              size="medium"
            />
          </div>
          <DreamJobButton {...props} />
          <ApplicationEllipsis {...props} />
        </div>

        <Stack gap="4px" className="justify-center">
          {application.dream_job && <Tag label={t("dream_job_tag")} color={theme.marigoldE50} />}
          <SectionHeader>{application.job_title}</SectionHeader>
          <div className="flex flex-wrap items-center gap-2">
            <SectionTitle>{application.company_name}</SectionTitle>
            {application.sharingStatus ? (
              <HoverCard>
                <HoverCardTrigger asChild>
                  <div className="flex items-center gap-2 cursor-pointer">
                    <CheckMarkCircleIcon size="sm" viewBox="0 0 16 16" color="green" />
                    <Body>{t("sharing_updates")}</Body>
                  </div>
                </HoverCardTrigger>
                <HoverCardContent>
                  {t("sharing_updates_tooltip", { company_name: application.company_name })}
                </HoverCardContent>
              </HoverCard>
            ) : (
              <div className="flex items-center gap-2">
                <HelpPopover
                  text={t("not_sharing_updates_tooltip", {
                    company_name: application.company_name,
                  })}
                  className="flex"
                />
                <Body className="!text-gray-800">{t("not_sharing_updates")}</Body>
              </div>
            )}
          </div>
        </Stack>
      </div>
      <div className="hidden md:flex items-center gap-2">
        <DreamJobButton {...props} />
        <ApplicationEllipsis {...props} />
      </div>
    </Row>
  );
};

export default ApplicationHeader;
